/* ============================================================================
   canvasStore.tsx — store partagé entre les 3 colonnes (left controls,
   center canvas, right chat). React Context + useReducer minimal,
   pas de lib externe (Zustand/Jotai = overkill pour ce scope).

   Utilisation :
     - <CanvasProvider> dans Home.tsx (au-dessus des 3 colonnes)
     - useCanvasParams() retourne [params, dispatch] pour controls panel + canvas
     - useHandTracking() retourne le state du hand tracking (frame courante)
   ============================================================================ */

import {
  createContext,
  useContext,
  useReducer,
  useState,
  useMemo,
  type ReactNode,
  type Dispatch,
} from 'react';

/* ----------------------------------------------------------------------------
   Types (source de vérité pour Left + Center)
   ---------------------------------------------------------------------------- */

export type CanvasMode =
  | 'image-to-ascii'
  | 'procedural'
  | 'marthe'
  | 'gallery'
  | 'fluid'
  | 'face'
  | 'face2'
  | 'fucked-up'
  | 'fairy-world';

/** Qualité de rendu du mode 'fairy-world' (pixelRatio + densité). */
export type FairyQualityId = 'eco' | 'normal' | 'max';

/** Durees auto-rotate (ms) pour le mode galerie. 0 = pause manuelle. */
export type GalleryAutoRotateMs = 0 | 3000 | 6000 | 12000;

/** Mode visuel du présence : isolated = vignettes sur fond noir,
 *  overlay = vignettes par-dessus glyph art existant. */
export type PresenceVisualMode = 'isolated' | 'overlay';

/** Styles composables superposés au mode présence (cumulables, pas exclusifs). */
export interface PresenceStyles {
  /** Surveillance : CCTV header + timestamp + confidence bar + scanlines. */
  surveillance: boolean;
  /** Terminal : scramble label + scanlines subtils. */
  terminal: boolean;
  /** Techno : glitch RGB pulse + corner ornaments + cyan accent rare. */
  techno: boolean;
}

export type GlyphSetId =
  | 'classic'
  | 'dense'
  | 'minimal'
  | 'fairy'
  | 'symbols'
  | 'blocks'
  | 'cyanotype'
  | 'halftone'
  | 'liquid';
export type PaletteId = 'mono-ink' | 'silver' | 'graphite' | 'stone' | 'ash';
export type BgId = 'paper' | 'ink';
export type OrnamentStyleId =
  | 'baroque-scroll'
  | 'floral-wreath'
  | 'vine-border'
  | 'arabesque-mirror';
export type SymmetryId = 'horizontal' | 'vertical' | 'asymmetric';

export interface CanvasParams {
  // Mode actif
  mode: CanvasMode;

  // Common (tous modes)
  glyphSet: GlyphSetId;
  density: number; // 40 → 200 (nb glyphes par rangée environ)
  fontSize: number; // 8 → 20 (px)
  palette: PaletteId;
  bgPalette: BgId;
  letterSpacing: number; // 0 → 4 (px)
  lineHeight: number; // 0.9 → 1.6

  // Mode image-to-ascii
  imageSrc: string | null; // data URL ou null
  contrast: number; // 0.5 → 2.0
  brightness: number; // 0.5 → 1.5
  invert: boolean;
  ditherAmount: number; // 0 → 1

  // Mode procedural
  seed: number; // uint32
  ornamentStyle: OrnamentStyleId;
  symmetry: SymmetryId;

  // Hand tracking (modulation runtime)
  handTrackingEnabled: boolean;
  handInfluence: number; // 0 → 1 — quantité de modulation par les doigts

  // Effets dérivés (manuel via slider OU live via hand tracking)
  scatterAmount: number; // 0 → 1 — éparpillement des glyphes hors de leur grille

  // Mode galerie : auto-rotation duree (0 = pause)
  galleryAutoRotateMs: GalleryAutoRotateMs;

  // Mode 'face2' (présence) : isolated (fond noir, vignettes seules)
  // ou overlay (par-dessus le glyph art existant)
  presenceMode: PresenceVisualMode;
  // Mode 'face2' : freeze positions et fragments (pour capture/screenshot)
  presenceFreeze: boolean;
  // Mode 'face2' : styles composables (cumulables)
  presenceStyles: PresenceStyles;

  // Mode 'fairy-world' : qualité de rendu (pixelRatio, MSAA, densités)
  fairyQuality: FairyQualityId;
}

export const DEFAULT_PARAMS: CanvasParams = {
  mode: 'image-to-ascii',
  glyphSet: 'classic',
  density: 100,
  fontSize: 11,
  palette: 'mono-ink',
  bgPalette: 'paper',
  letterSpacing: 0,
  lineHeight: 1.0,
  imageSrc: null,
  contrast: 1.1,
  brightness: 1.0,
  invert: false,
  ditherAmount: 0.3,
  seed: 1,
  ornamentStyle: 'baroque-scroll',
  symmetry: 'horizontal',
  handTrackingEnabled: false,
  handInfluence: 0.5,
  scatterAmount: 0,
  galleryAutoRotateMs: 6000,
  fairyQuality: 'normal',
  presenceMode: 'isolated',
  presenceFreeze: false,
  presenceStyles: {
    surveillance: false,
    terminal: false,
    techno: false,
  },
};

/* ----------------------------------------------------------------------------
   Hand tracking frame state (Center publie, Left lit)
   ---------------------------------------------------------------------------- */

export interface HandLandmark {
  x: number; // normalisé 0..1
  y: number; // normalisé 0..1
  z?: number;
}

export interface HandFrame {
  /** Centroïde paume normalisé [0..1] dans le viewport. null si pas de main détectée. */
  palm: { x: number; y: number } | null;
  /** Distance pouce-index normalisée 0..1 (pinch indicator). null si pas dispo. */
  pinch: number | null;
  /** Confidence 0..1 du tracking. */
  confidence: number;
  /** Timestamp ms de la frame. */
  ts: number;
  /** Liste des mains détectées : 0, 1 ou 2 mains. Chaque main = 21 landmarks. */
  landmarks: HandLandmark[][] | null;
}

export const DEFAULT_HAND_FRAME: HandFrame = {
  palm: null,
  pinch: null,
  confidence: 0,
  ts: 0,
  landmarks: null,
};

/* ----------------------------------------------------------------------------
   Reducer params
   ---------------------------------------------------------------------------- */

export type CanvasAction =
  | { type: 'set'; key: keyof CanvasParams; value: CanvasParams[keyof CanvasParams] }
  | { type: 'patch'; patch: Partial<CanvasParams> }
  | { type: 'reset' }
  | { type: 'reseed' };

function reducer(state: CanvasParams, action: CanvasAction): CanvasParams {
  switch (action.type) {
    case 'set':
      return { ...state, [action.key]: action.value };
    case 'patch':
      return { ...state, ...action.patch };
    case 'reset':
      return { ...DEFAULT_PARAMS };
    case 'reseed':
      return { ...state, seed: Math.floor(Math.random() * 0xffffffff) };
    default:
      return state;
  }
}

/* ----------------------------------------------------------------------------
   Context
   ---------------------------------------------------------------------------- */

interface CanvasContextValue {
  params: CanvasParams;
  dispatch: Dispatch<CanvasAction>;
  handFrame: HandFrame;
  setHandFrame: (frame: HandFrame) => void;
}

const CanvasContext = createContext<CanvasContextValue | null>(null);

export function CanvasProvider({ children }: { children: ReactNode }) {
  const [params, dispatch] = useReducer(reducer, DEFAULT_PARAMS);
  const [handFrame, setHandFrame] = useState<HandFrame>(DEFAULT_HAND_FRAME);
  const value = useMemo(
    () => ({ params, dispatch, handFrame, setHandFrame }),
    [params, handFrame],
  );
  return <CanvasContext.Provider value={value}>{children}</CanvasContext.Provider>;
}

export function useCanvasParams(): [CanvasParams, Dispatch<CanvasAction>] {
  const ctx = useContext(CanvasContext);
  if (!ctx) throw new Error('useCanvasParams must be used within <CanvasProvider>');
  return [ctx.params, ctx.dispatch];
}

export function useHandTracking(): {
  frame: HandFrame;
  setFrame: (f: HandFrame) => void;
} {
  const ctx = useContext(CanvasContext);
  if (!ctx) throw new Error('useHandTracking must be used within <CanvasProvider>');
  return { frame: ctx.handFrame, setFrame: ctx.setHandFrame };
}
